import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Box, Alert, CircularProgress, IconButton,
  Collapse, Tooltip, Card, CardContent, Grid, Stack, Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HubIcon from '@mui/icons-material/Hub';
import LanIcon from '@mui/icons-material/Lan';
import DnsIcon from '@mui/icons-material/Dns';
import StorageIcon from '@mui/icons-material/Storage';
import { networksApi } from '../api';

const driverColors = {
  overlay: 'primary',
  bridge: 'success',
  host: 'warning',
  null: 'default',
  none: 'default',
};

function Networks() {
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNetwork, setExpandedNetwork] = useState(null);

  const fetchNetworks = async () => {
    try {
      setLoading(true);
      const res = await networksApi.list();
      setNetworks(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch networks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNetworks(); }, []);

  const toggleExpand = (id) => {
    setExpandedNetwork(expandedNetwork === id ? null : id);
  };

  // Summary stats
  const overlayCount = networks.filter(n => n.driver === 'overlay').length;
  const bridgeCount = networks.filter(n => n.driver === 'bridge').length;
  const totalContainers = networks.reduce((sum, n) => sum + (n.containerCount || 0), 0);
  const totalServices = networks.reduce((sum, n) => sum + (n.serviceCount || 0), 0);

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HubIcon fontSize="large" color="primary" /> Networks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Docker networks and their attached containers &amp; services
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchNetworks} color="primary" size="large">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <HubIcon color="primary" />
              <Typography variant="h5">{networks.length}</Typography>
              <Typography variant="caption" color="text.secondary">Total Networks</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <LanIcon color="info" />
              <Typography variant="h5">{overlayCount}</Typography>
              <Typography variant="caption" color="text.secondary">Overlay</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <DnsIcon color="success" />
              <Typography variant="h5">{bridgeCount}</Typography>
              <Typography variant="caption" color="text.secondary">Bridge</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <StorageIcon color="secondary" />
              <Typography variant="h5">{totalContainers}</Typography>
              <Typography variant="caption" color="text.secondary">Attached Containers</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Driver</strong></TableCell>
                <TableCell><strong>Scope</strong></TableCell>
                <TableCell><strong>Subnet</strong></TableCell>
                <TableCell><strong>Gateway</strong></TableCell>
                <TableCell align="center"><strong>Containers</strong></TableCell>
                <TableCell align="center"><strong>Services</strong></TableCell>
                <TableCell><strong>Flags</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {networks.map((net) => (
                <React.Fragment key={net.id}>
                  <TableRow
                    hover
                    onClick={() => toggleExpand(net.id)}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedNetwork === net.id ? 'unset' : undefined } }}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedNetwork === net.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{net.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {net.id?.substring(0, 12)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={net.driver || 'null'}
                        size="small"
                        color={driverColors[net.driver] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{net.scope}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {net.ipam?.[0]?.subnet || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {net.ipam?.[0]?.gateway || '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={net.containerCount} size="small" color={net.containerCount > 0 ? 'info' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={net.serviceCount} size="small" color={net.serviceCount > 0 ? 'secondary' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {net.internal && <Chip label="Internal" size="small" variant="outlined" color="warning" />}
                        {net.attachable && <Chip label="Attachable" size="small" variant="outlined" color="info" />}
                        {net.ingress && <Chip label="Ingress" size="small" variant="outlined" color="success" />}
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail row */}
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, px: 0 }}>
                      <Collapse in={expandedNetwork === net.id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 3, bgcolor: 'action.hover' }}>
                          <Grid container spacing={3}>
                            {/* Attached Containers */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StorageIcon fontSize="small" /> Attached Containers ({net.containers?.length || 0})
                              </Typography>
                              {net.containers?.length > 0 ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><strong>Name</strong></TableCell>
                                      <TableCell><strong>IPv4</strong></TableCell>
                                      <TableCell><strong>State</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {net.containers.map((c) => (
                                      <TableRow key={c.id}>
                                        <TableCell>
                                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                            {c.name || c.id?.substring(0, 12)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                          {c.iPv4Address || c.ipv4Address || '—'}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={c.state}
                                            size="small"
                                            color={c.state === 'running' ? 'success' : 'default'}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" color="text.secondary">No containers attached</Typography>
                              )}
                            </Grid>

                            {/* Attached Services */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LanIcon fontSize="small" /> Attached Services ({net.services?.length || 0})
                              </Typography>
                              {net.services?.length > 0 ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><strong>Service</strong></TableCell>
                                      <TableCell><strong>Virtual IP</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {net.services.map((s) => (
                                      <TableRow key={s.id}>
                                        <TableCell>
                                          <Typography variant="body2">{s.name || s.id?.substring(0, 12)}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                          {s.virtualIP || '—'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" color="text.secondary">No services attached</Typography>
                              )}
                            </Grid>

                            {/* Network Details */}
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="subtitle2" gutterBottom>Network Details</Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="caption" color="text.secondary">Full ID</Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                    {net.id}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="caption" color="text.secondary">Created</Typography>
                                  <Typography variant="body2">
                                    {net.created ? new Date(net.created).toLocaleString() : '—'}
                                  </Typography>
                                </Grid>
                                {net.ipam?.map((cfg, i) => (
                                  <React.Fragment key={i}>
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary">Subnet</Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cfg.subnet || '—'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary">Gateway</Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cfg.gateway || '—'}</Typography>
                                    </Grid>
                                    {cfg.ipRange && (
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">IP Range</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cfg.ipRange}</Typography>
                                      </Grid>
                                    )}
                                  </React.Fragment>
                                ))}
                                {net.options && Object.keys(net.options).length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Options</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                      {Object.entries(net.options).map(([k, v]) => (
                                        <Chip key={k} label={`${k}=${v}`} size="small" variant="outlined" />
                                      ))}
                                    </Stack>
                                  </Grid>
                                )}
                                {net.labels && Object.keys(net.labels).length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Labels</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                      {Object.entries(net.labels).map(([k, v]) => (
                                        <Chip key={k} label={`${k}=${v}`} size="small" variant="outlined" />
                                      ))}
                                    </Stack>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {networks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No networks found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default Networks;
